import Tracker from "../tracker.js";
import {EVENT, LOOPTYPE, TRACKERMODE} from "../enum.js";
import Instrument from "../models/instrument.js";
import Note from "../models/note.js";
import EventBus from "../eventBus.js";
import {BinaryStream} from "../filesystem.js";

// DBM0 (DigiBooster Pro / DigiBooster 3) module loader
// Format spec: http://digibooster.de/en/format.php
// Chunk-based IFF format, big-endian, ProTracker-compatible effects.

var DigiBooster = function(){
	var me = {};

	me.load = function(file, name){
		console.log("loading DigiBooster DBM0");
		file.goto(0);
		Tracker.setTrackerMode(TRACKERMODE.PROTRACKER, true);
		Tracker.useLinearFrequency = false;
		Tracker.clearInstruments(1);

		// DBM0 is big-endian
		file.litteEndian = false;

		var song = {
			patterns: [],
			restartPosition: 0,
			type: "DBM0"
		};

		// ---- Parse file header (8 bytes) ----
		var sig = file.readString(4); // "DBM0"
		if (sig !== "DBM0") {
			console.error("Not a DBM0 file: " + sig);
			return song;
		}
		var version = file.readUbyte(); // BCD version
		var revision = file.readUbyte(); // BCD revision
		file.jump(2); // reserved

		var isVersion2 = (version < 3); // for panning envelope scaling

		// ---- Parse chunks ----
		var info = null;
		var moduleName = "";
		var songs = [];
		var instruments = [];
		var patterns = [];
		var samples = [];
		var volEnvelopes = [];
		var panEnvelopes = [];

		while (file.index < file.length) {
			var chunkId = file.readString(4);
			var chunkLen = file.readDWord();
			var chunkEnd = file.index + chunkLen;

			if (chunkEnd > file.length) break;

			switch (chunkId) {
				case "NAME":
					moduleName = file.readString(44);
					break;

				case "INFO":
					info = {
						instruments: file.readWord(),
						samples: file.readWord(),
						songs: file.readWord(),
						patterns: file.readWord(),
						tracks: file.readWord()
					};
					break;

				case "SONG":
					for (var s = 0; s < info.songs; s++) {
						var songName = file.readString(44);
						var songLen = file.readWord();
						var playlist = [];
						for (var p = 0; p < songLen; p++) {
							playlist.push(file.readWord());
						}
						songs.push({ name: songName, length: songLen, playlist: playlist });
					}
					break;

				case "INST":
					for (var i = 0; i < info.instruments; i++) {
						var instName = file.readString(30);
						var sampleNum = file.readWord(); // 1-indexed
						var volume = file.readWord(); // 0-64
						var sampleRate = file.readDWord(); // C-4 rate in Hz
						var loopStart = file.readDWord();
						var loopLength = file.readDWord();
						var panning = file.readShort(); // -128 to +128
						var flags = file.readWord(); // bit 0: forward loop, bit 1: ping-pong loop

						instruments.push({
							name: instName,
							sampleNum: sampleNum,
							volume: volume,
							sampleRate: sampleRate,
							loopStart: loopStart,
							loopLength: loopLength,
							panning: panning,
							flags: flags
						});
					}
					break;

				case "PATT":
					for (var pat = 0; pat < info.patterns; pat++) {
						var rows = file.readWord();
						var dataLen = file.readDWord();
						var patData = parsePattern(file, rows, dataLen, info.tracks);
						patterns.push(patData);
						// Align to 2 bytes
						if (dataLen & 1) file.jump(1);
					}
					break;

				case "SMPL":
					for (var sm = 0; sm < info.samples; sm++) {
						var sFlags = file.readDWord();
						var sLength = file.readDWord();
						var bits = (sFlags & 1) ? 8 : (sFlags & 2) ? 16 : (sFlags & 4) ? 32 : 8;

						var sample = {
							length: sLength,
							bits: bits,
							data: []
						};

						if (bits === 16) {
							for (var si = 0; si < sLength; si++) {
								var w = file.readShort();
								sample.data.push(w / 32768);
							}
						} else if (bits === 8) {
							for (var si = 0; si < sLength; si++) {
								var b = file.readByte();
								sample.data.push(b / 128);
							}
						} else {
							// 32-bit — skip for now, treat as 16-bit
							for (var si = 0; si < sLength; si++) {
								file.jump(4);
							}
						}
						samples.push(sample);
					}
					break;

				case "VENV":
					var envCount = file.readWord();
					for (var e = 0; e < envCount; e++) {
						var envInst = file.readWord();
						var envFlags = file.readUbyte();
						var envPoints = file.readUbyte(); // sections = points - 1
						var envS1 = file.readUbyte();
						var envL1 = file.readUbyte();
						var envL2 = file.readUbyte();
						var envS2 = file.readUbyte();
						var nodes = [];
						for (var n = 0; n < 32; n++) {
							var pos = file.readWord();
							var val = file.readWord();
							nodes.push([pos, val]);
						}
						volEnvelopes.push({
							instrument: envInst,
							flags: envFlags,
							points: envPoints + 1,
							sustain1: envS1,
							loopStart: envL1,
							loopEnd: envL2,
							sustain2: envS2,
							nodes: nodes
						});
					}
					break;

				case "PENV":
					var pEnvCount = file.readWord();
					for (var e = 0; e < pEnvCount; e++) {
						var pEnvInst = file.readWord();
						var pEnvFlags = file.readUbyte();
						var pEnvPoints = file.readUbyte();
						var pEnvS1 = file.readUbyte();
						var pEnvL1 = file.readUbyte();
						var pEnvL2 = file.readUbyte();
						var pEnvS2 = file.readUbyte();
						var pNodes = [];
						for (var n = 0; n < 32; n++) {
							var pPos = file.readWord();
							var pVal = file.readShort();
							// Version 2 scales panning from [0,64] to [-128,+128]
							if (isVersion2) {
								pVal = (pVal - 32) * 4; // approximate reverse scaling
							}
							pNodes.push([pPos, pVal]);
						}
						panEnvelopes.push({
							instrument: pEnvInst,
							flags: pEnvFlags,
							points: pEnvPoints + 1,
							sustain1: pEnvS1,
							loopStart: pEnvL1,
							loopEnd: pEnvL2,
							sustain2: pEnvS2,
							nodes: pNodes
						});
					}
					break;

				case "DSPE":
					// Echo DSP — skip for now
					break;

				case "PNAM":
					// Pattern names — skip
					break;

				default:
					// Unknown chunk — skip
					break;
			}

			file.goto(chunkEnd);
		}

		if (!info) {
			console.error("DBM0: No INFO chunk found");
			return song;
		}

		// ---- Build song structure ----
		Tracker.setTrackCount(info.tracks);
		song.channels = info.tracks;

		// Use first song's playlist
		if (songs.length > 0) {
			song.patternTable = songs[0].playlist;
			song.length = songs[0].length;
		} else {
			song.patternTable = [0];
			song.length = 1;
		}

		// Patterns
		song.patterns = patterns;

		// ---- Build instruments ----
		// Each DBM instrument references a sample (1-indexed)
		var instrumentContainer = [];
		for (var i = 0; i < info.instruments; i++) {
			var instData = instruments[i];
			var inst = Instrument();
			inst.name = instData.name;
			inst.samples = [];

			var sampleIdx = instData.sampleNum - 1; // convert to 0-indexed
			var sample = samples[sampleIdx];
			if (!sample) {
				// Create empty sample
				sample = { length: 0, bits: 8, data: [] };
			}

			// Set sample properties from instrument
			sample.length = sample.length || 0;
			sample.sampleRate = instData.sampleRate;
			sample.volume = instData.volume;
			sample.panning = instData.panning;

			// Loop
			if (instData.loopLength > 0) {
				sample.loop = {
					start: instData.loopStart,
					length: instData.loopLength,
					type: (instData.flags & 2) ? LOOPTYPE.PINGPONG : LOOPTYPE.FORWARD
				};
			} else {
				sample.loop = { start: 0, length: 0, type: LOOPTYPE.NONE };
			}

			// Relative note: DBM uses C-4 as base, same as ProTracker
			sample.relativeNote = 0;

			inst.samples.push(sample);
			inst.sample = sample;
			inst.sampleNumberForNotes = [];
			for (var n = 0; n  < 96; n++) inst.sampleNumberForNotes.push(0);
			inst.setSampleIndex(0);

			// Apply volume envelope if exists
			for (var ve = 0; ve < volEnvelopes.length; ve++) {
				if (volEnvelopes[ve].instrument === i + 1) {
					var env = volEnvelopes[ve];
					inst.volumeEnvelope.enabled = (env.flags & 1) !== 0;
					inst.volumeEnvelope.sustain = (env.flags & 2) !== 0;
					inst.volumeEnvelope.loop = (env.flags & 4) !== 0;
					inst.volumeEnvelope.sustainPoint = env.sustain1;
					inst.volumeEnvelope.loopStartPoint = env.loopStart;
					inst.volumeEnvelope.loopEndPoint = env.loopEnd;
					inst.volumeEnvelope.points = env.nodes.slice(0, env.points);
					inst.volumeEnvelope.count = env.points;
					break;
				}
			}

			// Apply panning envelope if exists
			for (var pe = 0; pe < panEnvelopes.length; pe++) {
				if (panEnvelopes[pe].instrument === i + 1) {
					var pEnv = panEnvelopes[pe];
					inst.panningEnvelope.enabled = (pEnv.flags & 1) !== 0;
					inst.panningEnvelope.sustain = (pEnv.flags & 2) !== 0;
					inst.panningEnvelope.loop = (pEnv.flags & 4) !== 0;
					inst.panningEnvelope.sustainPoint = pEnv.sustain1;
					inst.panningEnvelope.loopStartPoint = pEnv.loopStart;
					inst.panningEnvelope.loopEndPoint = pEnv.loopEnd;
					inst.panningEnvelope.points = pEnv.nodes.slice(0, pEnv.points);
					inst.panningEnvelope.count = pEnv.points;
					break;
				}
			}

			Tracker.setInstrument(i + 1, inst);
			instrumentContainer.push({ label: (i + 1) + " " + inst.name, data: i + 1 });
		}

		EventBus.trigger(EVENT.instrumentListChange, instrumentContainer);
		song.instruments = Tracker.getInstruments();

		// Set default tempo (DBM uses ProTracker defaults: speed 6, tempo 125)
		Tracker.setAmigaSpeed(6);
		Tracker.setBPM(125);

		// Set song name
		song.name = moduleName;
		song.title = moduleName;

		Tracker.setSong(song);
		EventBus.trigger(EVENT.songChange, song);
		return song;
	};

	// Parse a DBM0 packed pattern
	// Format: entries start with track number (1-based), 0 = row skip
	// Following byte is a bitmask: bit0=note, bit1=inst, bit2=cmd1, bit3=par1, bit4=cmd2, bit5=par2
	function parsePattern(file, rowCount, dataLen, trackCount) {
		var pattern = [];
		var end = file.index + dataLen;

		// Initialize all rows with empty notes for all tracks
		for (var r = 0; r < rowCount; r++) {
			var row = [];
			for (var t = 0; t < trackCount; t++) {
				row.push(Note());
			}
			pattern.push(row);
		}

		var row = 0;
		while (file.index < end && row < rowCount) {
			var track = file.readUbyte();
			if (track === 0) {
				row++;
				continue;
			}

			var mask = file.readUbyte();
			var t = track - 1; // convert to 0-indexed

			if (t >= trackCount) {
				// Skip invalid track
				if (mask & 1) file.jump(1);
				if (mask & 2) file.jump(1);
				if (mask & 4) file.jump(1);
				if (mask & 8) file.jump(1);
				if (mask & 16) file.jump(1);
				if (mask & 32) file.jump(1);
				continue;
			}

			var note = pattern[row][t];

			if (mask & 1) {
				// Note: two nibbles — high = octave (1-7), low = halftone (0-11)
				var noteByte = file.readUbyte();
				var octave = (noteByte >> 4) & 0x0f;
				var halftone = noteByte & 0x0f;
				if (octave >= 1 && octave <= 7 && halftone <= 11) {
					// Convert to note index: (octave-1)*12 + halftone + 1 (1-indexed for our engine)
					var noteIndex = (octave - 1) * 12 + halftone + 1;
					note.setIndex(noteIndex);
				}
			}
			if (mask & 2) {
				note.instrument = file.readUbyte();
			}
			if (mask & 4) {
				note.effect = file.readUbyte();
			}
			if (mask & 8) {
				note.param = file.readUbyte();
			}
			if (mask & 16) {
				// Second command — store in effect2/param2
				note.effect2 = file.readUbyte();
			}
			if (mask & 32) {
				note.param2 = file.readUbyte();
			}
		}

		// Make sure we're at the end of pattern data
		file.goto(end);

		return pattern;
	}

	return me;
};

export default DigiBooster;
